import { useEffect, useRef } from 'react';

import { getSwipeDirection } from './get-swipe-direction';

type Args = {
    onNext: () => void;
    onPrevious: () => void;
};

export function useMonthSwipe({ onNext, onPrevious }: Args) {
    const releaseGesture = useRef(() => {});

    useEffect(() => () => releaseGesture.current(), []);

    function onPointerDown(event: React.PointerEvent) {
        if (!event.isPrimary) return;
        // A prior gesture whose release never reached `window` (e.g. button let go outside the
        // browser window) would otherwise leave its listeners attached for this pointerId.
        releaseGesture.current();

        const pointerId = event.pointerId;
        const start = { x: event.clientX, y: event.clientY };

        function onPointerUp(event: PointerEvent) {
            if (event.pointerId !== pointerId) return;
            releaseGesture.current();

            const direction = getSwipeDirection(start, { x: event.clientX, y: event.clientY });
            if (direction === null) return;

            // react-day-picker skips its month animation while a day is focused, and a tapped day stays focused.
            if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
            if (direction === 'next') onNext();
            if (direction === 'previous') onPrevious();
        }

        function onPointerCancel(event: PointerEvent) {
            if (event.pointerId !== pointerId) return;
            releaseGesture.current();
        }

        // On window, not the wrapper, so a drag that leaves it still ends here; no pointer capture,
        // which would retarget the click and break day taps.
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerCancel);
        releaseGesture.current = () => {
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerCancel);
        };
    }

    return { onPointerDown };
}
